import * as React from "react";
import { useSearchParams } from "react-router";

import { useLaunchIntents } from "@/app/recsys/launch/useLaunchIntents";
import { useLaunchLink } from "@/app/recsys/launch/useLaunchLink";

type BeginCreate = (profileId: string, phone: string) => void | Promise<void>;

/**
 * Partner-launch wiring: intents polling, deep-link consume, auto-start token.
 */
export function usePartnerLaunch(options: {
  isAuthenticated: boolean;
  isLoadingProfiles: boolean;
  copilotEnabled: boolean;
  isRecording: boolean;
  onCreateProfile: () => string;
  startCreateForProfile: BeginCreate;
}) {
  const {
    isAuthenticated,
    isLoadingProfiles,
    copilotEnabled,
    isRecording,
    onCreateProfile,
    startCreateForProfile,
  } = options;

  const isRecordingRef = React.useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [launchAutoStart, setLaunchAutoStart] = React.useState<string | null>(null);
  const [launchError, setLaunchError] = React.useState<string | null>(null);
  const launchAdoptSessionIdRef = React.useRef<string | null>(null);

  const beginLaunch = React.useCallback(
    (target: { phone: string; sessionId: string | null }) => {
      const profileId = onCreateProfile();
      void startCreateForProfile(profileId, target.phone);
      if (target.sessionId) launchAdoptSessionIdRef.current = target.sessionId;
      setLaunchAutoStart(target.sessionId ?? profileId);
    },
    [onCreateProfile, startCreateForProfile],
  );

  React.useEffect(() => {
    const token = searchParams.get("token");
    if (token) sessionStorage.setItem("launch_token", token);
  }, [searchParams]);

  React.useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const launch = useLaunchIntents({
    enabled: isAuthenticated && Boolean(copilotEnabled),
    busy: isRecording,
    onLaunch: (intent) =>
      beginLaunch({ phone: intent.client_phone, sessionId: intent.session_id }),
  });

  useLaunchLink({
    ready: isAuthenticated && !isLoadingProfiles,
    isRecordingRef,
    adopt: launch.adopt,
    beginLaunch,
    onError: setLaunchError,
    onConsumed: () => setSearchParams({}, { replace: true }),
  });

  return {
    launchAutoStart,
    launchError,
    setLaunchError,
    launchAdoptSessionIdRef,
    confirmStarted: launch.confirmStarted,
    fail: launch.fail,
  };
}
